'use client';

import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { useT } from '@/components/i18n-provider';
import { normalizeBindingPath, isReservedBindingPath } from '@/lib/domains/app-routing';

/**
 * "Which app answers which path" for one verified custom domain.
 *
 *   mydomain.com/      → Landing
 *   mydomain.com/shop  → Shop
 *
 * Rendered under a verified domain in Settings → Domains. Deliberately a small
 * list plus one add row rather than a dialog: a domain usually has one or two
 * routes, and a dialog for a two-field form is more chrome than content.
 *
 * Path validation is imported from the same module the server and the proxy
 * use, so what the field accepts is exactly what will be stored and later
 * matched. The server re-validates regardless — this is for feedback, not
 * enforcement.
 */

interface Binding {
  id: string;
  path: string;
  contentId: string;
  appName: string | null;
}

interface AppOption {
  id: string;
  displayName: string;
}

/**
 * What a domain can serve, split by whether it KEEPS anything.
 *
 * A **project** is a saved document (an ADR-0022 project folder: a folder
 * carrying `metadata.projectMime`, which names the editor that opens it). The
 * collab room is `<folderId>:MAIN`, so it is the same document on every visit,
 * and the folder's own ACL decides who may read or write it.
 *
 * An **app** is the catalog row. `/w/<catalog>` deliberately mints a fresh
 * scratch room per mount — right for a game or a demo, where each visitor
 * should get their own instance, and wrong for anything the owner expects to
 * still be there tomorrow.
 *
 * That distinction is the whole reason both are offered instead of only apps:
 * linking the Docs *app* and then typing into it looks like data loss, because
 * the next reload lands in a different empty room. Linking a Docs *project*
 * does what people mean.
 */
const WORKSPACE_PROJECT_MIME = 'application/vnd.robutler.workspace+json';

export function DomainAppBindings({
  verificationId,
  domain,
  readOnly = false,
}: {
  verificationId: string;
  domain: string;
  /**
   * A platform-issued app host (app-hosts plan B4): the list is shown, the
   * add row and the remove buttons are not, and the pickers are never
   * fetched. The API refuses the writes independently.
   */
  readOnly?: boolean;
}) {
  const t = useT();
  const [bindings, setBindings] = useState<Binding[]>([]);
  const [projects, setProjects] = useState<AppOption[]>([]);
  const [apps, setApps] = useState<AppOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [path, setPath] = useState('/');
  const [contentId, setContentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `cache: 'no-store'` is load-bearing, not belt-and-braces. `load()` runs
      // again right after a successful add/remove, and without it the browser
      // replays its cached copy of the GET from a moment earlier: the binding
      // saves, but the list still reads "No apps linked yet" until a full page
      // reload. Observed live on dev while linking Docs to deepattribution.com.
      const [bindingRes, appRes, projectRes] = await Promise.all([
        fetch(`/api/domains/${verificationId}/bindings`, {
          credentials: 'same-origin',
          cache: 'no-store',
        }),
        // The pickers exist to add a binding; a read-only host never adds one.
        readOnly
          ? null
          : fetch('/api/content?type=widget&owner=me&limit=100', {
              credentials: 'same-origin',
              cache: 'no-store',
            }),
        // The flat owned feed (no `parentId`) surfaces project folders as
        // top-level rows and hides their internals, which is exactly the
        // "my projects" list.
        readOnly
          ? null
          : fetch('/api/content?owner=me&limit=200', {
              credentials: 'same-origin',
              cache: 'no-store',
            }),
      ]);
      if (bindingRes.ok) {
        const data = (await bindingRes.json()) as { bindings?: Binding[] };
        setBindings(data.bindings ?? []);
      }
      if (appRes?.ok) {
        // `/api/content` returns `items`, not `content` — reading the wrong key
        // left the picker silently empty, so nothing could ever be linked.
        const data = (await appRes.json()) as {
          items?: Array<{ id: string; displayName?: string; originalName?: string }>;
        };
        setApps(
          (data.items ?? []).map((c) => ({
            id: c.id,
            displayName: c.displayName || c.originalName || 'Untitled app',
          })),
        );
      }
      if (projectRes?.ok) {
        const data = (await projectRes.json()) as {
          items?: Array<{
            id: string;
            displayName?: string;
            originalName?: string;
            contentType?: string;
            metadata?: { projectMime?: unknown } | null;
          }>;
        };
        setProjects(
          (data.items ?? [])
            .filter((c) => {
              // A project folder is a folder that names its editor. Workspaces
              // carry a projectMime too but are browsing containers, not app
              // saves — serving one at a domain is not a coherent thing to do.
              if (c.contentType !== 'folder') return false;
              const mime = c.metadata?.projectMime;
              return typeof mime === 'string' && mime !== WORKSPACE_PROJECT_MIME;
            })
            .map((c) => ({
              id: c.id,
              displayName: c.displayName || c.originalName || 'Untitled project',
            })),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [verificationId, readOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live feedback on the path field. The server is still the authority.
  const normalized = normalizeBindingPath(path);
  const pathProblem = !path.trim()
    ? null
    : !normalized
      ? t('settings.domainPathPlain')
      : isReservedBindingPath(normalized)
        ? t('settings.domainPathReserved', { path: normalized })
        : null;

  const add = async () => {
    if (!contentId || !normalized || pathProblem) return;
    setAdding(true);
    setError(null);
    try {
      const res = await fetch(`/api/domains/${verificationId}/bindings`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ path: normalized, contentId }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(data?.error ?? t('settings.domainLinkFailed'));
        return;
      }
      setPath('/');
      setContentId('');
      await load();
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id: string) => {
    setError(null);
    const res = await fetch(`/api/domains/${verificationId}/bindings/${id}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      setError(t('settings.domainUnlinkFailed'));
      return;
    }
    await load();
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{t('settings.domainAppsTitle')}</span>
        {loading && <Spinner size="xs" />}
      </div>

      {!loading && bindings.length === 0 && (
        <p className="text-sm text-muted-foreground mb-3">
          {t('settings.domainAppsEmpty')}
        </p>
      )}

      {bindings.length > 0 && (
        <ul className="space-y-1 mb-3">
          {bindings.map((b) => (
            <li
              key={b.id}
              className="flex items-center justify-between gap-2 text-sm bg-muted/40 rounded px-3 py-2"
            >
              <span className="flex items-center gap-2 min-w-0">
                <a
                  href={`https://${domain}${b.path === '/' ? '' : b.path}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-primary hover:underline flex items-center gap-1 truncate"
                >
                  {domain}
                  {b.path === '/' ? '/' : b.path}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
                <span className="text-muted-foreground shrink-0">→</span>
                <span className="truncate">{b.appName ?? t('settings.domainUntitledApp')}</span>
              </span>
              {!readOnly && (
                <button
                  onClick={() => remove(b.id)}
                  aria-label={t('settings.domainRemoveBinding', { path: b.path })}
                  className="btn-ghost text-destructive hover:bg-destructive/10 p-1 shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-start gap-2">
          <div className="flex flex-col">
            <input
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/shop"
              aria-label={t('settings.domainPathLabel')}
              className="input text-sm w-32"
            />
            {pathProblem && (
              <span className="text-xs text-destructive mt-1">{pathProblem}</span>
            )}
          </div>
          <select
            value={contentId}
            onChange={(e) => setContentId(e.target.value)}
            aria-label={t('settings.domainProjectOrApp')}
            className="input text-sm flex-1 min-w-[12rem]"
          >
            <option value="">{t('settings.domainChooseTarget')}</option>
            {projects.length > 0 && (
              <optgroup label={t('settings.domainGroupProjects')}>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </optgroup>
            )}
            {apps.length > 0 && (
              <optgroup label={t('settings.domainGroupApps')}>
                {apps.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <button
            onClick={add}
            disabled={adding || !contentId || !normalized || !!pathProblem}
            className="btn-secondary text-sm flex items-center gap-1"
          >
            {adding ? <Spinner size="xs" /> : <Plus className="h-3 w-3" />}
            {t('settings.domainLinkCta')}
          </button>
        </div>
      )}

      {error && <p className="text-sm text-destructive mt-2">{error}</p>}

      {!readOnly && (
        <p className="text-xs text-muted-foreground mt-2">
          {t('settings.domainBindingHelp')}
        </p>
      )}
    </div>
  );
}
